// src/components/sourcex/QuestionField.jsx
//
// SourceX Roadmap — PHASE 0: the single field renderer.
//
// Before this, SectionEditor.js and PatientSource.js each carried their own
// near-identical switch over question types. Any new type had to be added in
// two places and they had already begun to drift (unit rendering, wrapper
// markup). This is now the ONE place a source-document field is rendered.
//
// Controlled component: it owns no state. `value` in, `onChange(next)` out —
// so the caller decides where the answer is persisted (the template doc, a
// patient doc, or nowhere at all in preview mode). That separation is what
// lets the template editor render a real-looking form without ever recording
// an answer into the shared template. See `readOnly`.

import { Plus, Trash2 } from "lucide-react";
import { useDialog } from "./useDialog";
import {
  normFieldType,
  emptyValueFor,
  makeColumn,
  isCompositeTable,
} from "../../services/templateSchema";

export const inputCls =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition [color-scheme:dark] disabled:opacity-50 disabled:cursor-not-allowed";
export const selectCls =
  "appearance-none px-3 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 cursor-pointer transition disabled:opacity-50 disabled:cursor-not-allowed";

const isChecked = (v) => v === true || v === "true";

/**
 * Render the input control for one question.
 *
 * @param {object}   props
 * @param {object}   props.question  a v2 question (see services/templateSchema)
 * @param {*}        props.value     current answer
 * @param {Function} props.onChange  (nextValue) => void
 * @param {boolean}  props.readOnly  render the control but ignore edits — used
 *                                   by the template editor, where typing a
 *                                   clinical value would write PHI into a doc
 *                                   shared by every patient in the study.
 * @param {Function} [props.onQuestionChange] (patch) => void — for edits to the
 *                                   question's own metadata rather than its
 *                                   answer. Currently only the unit toggle:
 *                                   which unit a measurement was taken in is a
 *                                   property of the field, not of the number.
 *                                   Omit it and the toggle renders read-only.
 */
export default function QuestionField({
  question,
  value,
  onChange,
  onQuestionChange,
  readOnly = false,
}) {
  const q = question || {};
  const type = normFieldType(q.type);
  const emit = (v) => {
    if (!readOnly && typeof onChange === "function") onChange(v);
  };
  const emitQuestion = (patch) => {
    if (!readOnly && typeof onQuestionChange === "function")
      onQuestionChange(patch);
  };

  switch (type) {
    case "DateTime":
      return (
        <input
          type="datetime-local"
          value={value || ""}
          disabled={readOnly}
          onChange={(e) => emit(e.target.value)}
          className={`${inputCls} mt-1`}
        />
      );

    case "Date":
      return (
        <input
          type="date"
          value={value || ""}
          disabled={readOnly}
          onChange={(e) => emit(e.target.value)}
          className={`${inputCls} mt-1`}
        />
      );

    case "Time":
      return (
        <input
          type="time"
          value={value || ""}
          disabled={readOnly}
          onChange={(e) => emit(e.target.value)}
          className={`${inputCls} mt-1`}
        />
      );

    case "Number":
      // A unit TOGGLE (kg/lbs, °C/°F) is a distinct control from a fixed unit
      // label: the chosen unit changes what the number means, so it has to be
      // recorded, not just displayed. Selecting a unit deliberately does NOT
      // convert the entered number — silently rewriting a recorded clinical
      // measurement is exactly the kind of thing a source document must not do.
      return (
        <div className="mt-1 flex items-center gap-2">
          <input
            type="number"
            inputMode="decimal"
            value={value ?? ""}
            disabled={readOnly}
            onChange={(e) => emit(e.target.value)}
            className={`${inputCls} flex-1`}
            placeholder="Enter value"
          />
          {Array.isArray(q.units) && q.units.length > 1 ? (
            <select
              value={q.unit || q.units[0]}
              disabled={readOnly || typeof onQuestionChange !== "function"}
              onChange={(e) => emitQuestion({ unit: e.target.value })}
              className={`${selectCls} shrink-0`}
              aria-label="Unit"
            >
              {q.units.map((u) => (
                <option key={u} value={u} className="bg-[#0b0820]">
                  {u}
                </option>
              ))}
            </select>
          ) : (
            q.unit && (
              <span className="text-sm text-slate-400 shrink-0">{q.unit}</span>
            )
          )}
        </div>
      );

    case "Checkbox":
      return (
        <label className="mt-1 inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isChecked(value)}
            disabled={readOnly}
            onChange={(e) => emit(e.target.checked)}
            className="w-4 h-4 rounded border-white/20 bg-white/[0.04] accent-purple-500"
          />
          <span className="text-sm text-slate-300">
            {isChecked(value) ? "Yes" : "No"}
          </span>
        </label>
      );

    case "Select":
      return (
        <select
          value={value || ""}
          disabled={readOnly}
          onChange={(e) => emit(e.target.value)}
          className={`${selectCls} w-full mt-1`}
        >
          <option value="" className="bg-[#0b0820]">
            Select
          </option>
          {(q.options || []).map((opt) => (
            <option key={opt} value={opt} className="bg-[#0b0820]">
              {opt}
            </option>
          ))}
        </select>
      );

    case "YesNo/Maybe":
      return (
        <select
          value={value || ""}
          disabled={readOnly}
          onChange={(e) => emit(e.target.value)}
          className={`${selectCls} w-full mt-1`}
        >
          <option value="" className="bg-[#0b0820]">
            Select
          </option>
          <option value="Yes" className="bg-[#0b0820]">
            Yes
          </option>
          <option value="No" className="bg-[#0b0820]">
            No
          </option>
          <option value="N/A" className="bg-[#0b0820]">
            N/A
          </option>
        </select>
      );

    case "MultiSelect": {
      // A checklist, not a multi-select <select>. eCRF checklists are read and
      // filled by eye during a visit; a native multi-select hides unchosen
      // options behind a scroll and makes ctrl-click a data-entry hazard.
      const selected = Array.isArray(value) ? value : [];
      const toggle = (opt) =>
        emit(
          selected.includes(opt)
            ? selected.filter((o) => o !== opt)
            : [...selected, opt]
        );
      return (
        <div className="mt-1 flex flex-col gap-1.5">
          {(q.options || []).map((opt) => (
            <label
              key={opt}
              className="inline-flex items-center gap-2 cursor-pointer select-none"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                disabled={readOnly}
                onChange={() => toggle(opt)}
                className="w-4 h-4 rounded border-white/20 bg-white/[0.04] accent-purple-500"
              />
              <span className="text-sm text-slate-300">{opt}</span>
            </label>
          ))}
          {(q.options || []).length === 0 && (
            <p className="text-xs text-slate-500 italic">
              No options defined for this checklist.
            </p>
          )}
        </div>
      );
    }

    case "TextArea":
      return (
        <textarea
          value={value || ""}
          disabled={readOnly}
          onChange={(e) => emit(e.target.value)}
          className={`${inputCls} mt-1 h-24`}
          placeholder="Enter response"
        />
      );

    case "Table":
      return (
        <TableField
          question={q}
          value={Array.isArray(value) ? value : []}
          onChange={emit}
          readOnly={readOnly}
        />
      );

    case "Signature":
      // Rendered as an inert block. Applying an actual electronic signature is
      // a Part 11 event — it needs an authenticated identity, a server-side
      // timestamp and an audit record, so it cannot be a text input the
      // coordinator types a name into. Wiring it to the existing e-sign flow
      // is deliberately left to the signature phase of the roadmap.
      return (
        <div className="mt-1 rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-3 py-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            {q.signatureRole || "Signature"}
          </div>
          {q.attestation && (
            <p className="mt-1 text-xs text-slate-400">{q.attestation}</p>
          )}
          <div className="mt-3 h-8 border-b border-white/20" />
          <div className="mt-1 flex justify-between text-[10px] text-slate-500">
            <span>Signature</span>
            <span>Date</span>
          </div>
        </div>
      );

    case "Header":
      // A source-only GROUP TITLE — the divider that visually separates one
      // form's block of questions from the next ("Vital Signs", "Adverse
      // Events"), optionally carrying the assessment's timing instruction. It
      // holds no answer, so it is a heading, not an input.
      return (
        <div className="mt-4 mb-1 flex items-center gap-3 first:mt-0">
          <span className="text-sm font-semibold tracking-wide text-purple-200 whitespace-normal">
            {q.question || "Section"}
          </span>
          <span className="flex-1 h-px bg-gradient-to-r from-purple-300/30 to-transparent" />
        </div>
      );

    case "Text":
    default:
      return (
        <input
          type="text"
          value={value || ""}
          disabled={readOnly}
          onChange={(e) => emit(e.target.value)}
          className={`${inputCls} mt-1`}
          placeholder="Enter response"
        />
      );
  }
}

/**
 * Repeatable rows — med history, adverse events, concomitant meds, per-system
 * physical exam. The answer is an array of {columnId: value} objects, so a
 * column can be renamed without orphaning the data under it.
 */
function TableField({ question, value, onChange, readOnly }) {
  const columns = (question.columns || []).map((c) => makeColumn(c));
  // A fixed-row table (per-system physical exam, a set lab panel) prints its
  // row labels and has exactly those rows — nothing to add or delete.
  const rowLabels = Array.isArray(question.rowLabels) ? question.rowLabels : [];
  const fixedRows = rowLabels.length > 0;
  const rows = value;
  const maxRows = Number.isInteger(question.maxRows) ? question.maxRows : 50;
  const minRows = Number.isInteger(question.minRows) ? question.minRows : 0;
  const { confirm: askConfirm, dialog } = useDialog();

  const setCell = (rowIndex, colId, cellValue) => {
    const next = [...rows];
    // A composite question (see isCompositeTable) starts with zero rows —
    // emptyValueFor("Table") is [] — so its one implied row doesn't exist
    // until the first answer is typed. Materialize any rows up to it rather
    // than requiring a separate "add row" step nobody using a composite
    // question would expect.
    while (next.length <= rowIndex) {
      const blank = {};
      columns.forEach((c) => {
        blank[c.id] = emptyValueFor(c.type);
      });
      next.push(blank);
    }
    next[rowIndex] = { ...next[rowIndex], [colId]: cellValue };
    onChange(next);
  };

  const addRow = () => {
    if (rows.length >= maxRows) return;
    const blank = {};
    columns.forEach((c) => {
      blank[c.id] = emptyValueFor(c.type);
    });
    onChange([...rows, blank]);
  };

  const removeRow = async (rowIndex) => {
    if (rows.length <= minRows) return;
    // A source document is a regulated record: rows are removed only on an
    // explicit, confirmed action, never as a side effect of an edit. Same
    // guard as before — only the dialog it asks with has changed.
    const ok = await askConfirm("Anything entered in it will be lost.", {
      title: "Remove this row?",
      confirmLabel: "Remove",
      tone: "danger",
    });
    if (!ok) return;
    onChange(rows.filter((_, i) => i !== rowIndex));
  };

  if (columns.length === 0) {
    return (
      <p className="mt-1 text-xs text-rose-300 italic">
        This table has no columns defined — edit the template to add them.
      </p>
    );
  }

  // A composite question — several answer pieces bundled under one question —
  // is a real Table (one row, no row labels) under the hood, but is filled in
  // as a stack of labeled fields, not a one-row grid. See isCompositeTable.
  if (isCompositeTable(question)) {
    const row = rows[0] || {};
    return (
      <div className="mt-1 rounded-xl border border-white/10 divide-y divide-white/10 overflow-hidden">
        {columns.map((c) => (
          <div key={c.id} className="flex items-center gap-3 px-3 py-2">
            <span className="w-32 shrink-0 text-xs font-medium text-slate-300">
              {c.label || "(untitled)"}
              {c.unit ? <span className="ml-1 text-slate-500">({c.unit})</span> : null}
            </span>
            <div className="flex-1 min-w-0">
              <QuestionField
                question={c}
                value={row[c.id]}
                onChange={(v) => setCell(0, c.id, v)}
                readOnly={readOnly}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Many eCRF logs (an Adverse Events table can be 25 columns) are far wider
  // than the panel. The wrapper scrolls horizontally INSIDE the card so the
  // table never stretches the page sideways.
  //
  // Layout is FIXED with an explicit width per column (via <colgroup> +
  // table-layout:fixed). Without this, the browser's auto layout fights the
  // full-width inputs in each cell and collapses columns until header text
  // wraps one letter per line and becomes unreadable. Fixed widths + a table
  // min-width sized to the column count keep every column legible and the whole
  // thing horizontally scrollable.
  const COL_REM = 12; // per data column
  const ACT_REM = 2.75; // trailing "remove row" column
  const tableMinWidth = `${columns.length * COL_REM + (readOnly ? 0 : ACT_REM)}rem`;
  return (
    <div className="mt-1 min-w-0">
      {/* Centered confirm, instead of the browser's top-anchored one. */}
      {dialog}
      <div className="max-w-full overflow-x-auto rounded-xl border border-white/10">
        <table
          className="text-sm border-collapse"
          style={{ tableLayout: "fixed", minWidth: tableMinWidth }}
        >
          <colgroup>
            {fixedRows && <col style={{ width: `${COL_REM + 4}rem` }} />}
            {columns.map((c) => (
              <col key={c.id} style={{ width: `${COL_REM}rem` }} />
            ))}
            {!readOnly && !fixedRows && <col style={{ width: `${ACT_REM}rem` }} />}
          </colgroup>
          <thead>
            <tr className="bg-white/[0.04]">
              {fixedRows && (
                <th className="text-left px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-semibold align-bottom border-b border-white/10 break-words">
                  {question.rowLabelHeader || ""}
                </th>
              )}
              {columns.map((c) => (
                <th
                  key={c.id}
                  className="text-left px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-semibold align-bottom border-b border-white/10 break-words"
                >
                  {c.label}
                  {c.unit ? (
                    <span className="ml-1 normal-case text-slate-500">
                      ({c.unit})
                    </span>
                  ) : null}
                </th>
              ))}
              {!readOnly && !fixedRows && (
                <th className="border-b border-white/10" />
              )}
            </tr>
          </thead>
          <tbody>
            {fixedRows &&
              rowLabels.map((label, ri) => {
                const row = rows[ri] || {};
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={ri} className="border-t border-white/5">
                    <td className="px-2 py-1 align-middle text-sm text-slate-200 break-words">
                      {label}
                    </td>
                    {columns.map((c) => (
                      <td key={c.id} className="px-1.5 py-1 align-top">
                        <QuestionField
                          question={c}
                          value={row[c.id]}
                          onChange={(v) => setCell(ri, c.id, v)}
                          readOnly={readOnly}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            {!fixedRows &&
              rows.map((row, ri) => (
              // Row identity is positional; rows carry no id of their own and
              // are only ever appended or explicitly removed.
              // eslint-disable-next-line react/no-array-index-key
              <tr key={ri} className="border-t border-white/5">
                {columns.map((c) => (
                  <td key={c.id} className="px-1.5 py-1 align-top">
                    <QuestionField
                      question={c}
                      value={row ? row[c.id] : undefined}
                      onChange={(v) => setCell(ri, c.id, v)}
                      readOnly={readOnly}
                    />
                  </td>
                ))}
                {!readOnly && (
                  <td className="px-1 align-middle text-center">
                    <button
                      type="button"
                      onClick={() => removeRow(ri)}
                      title="Remove row"
                      className="w-7 h-7 rounded-lg inline-flex items-center justify-center text-slate-500 hover:text-rose-300 hover:bg-rose-500/15 transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {!fixedRows && rows.length === 0 && (
              <tr className="border-t border-white/5">
                <td
                  colSpan={columns.length + (readOnly ? 0 : 1)}
                  className="px-2 py-3 text-xs text-slate-500 italic"
                >
                  No rows recorded.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!readOnly && !fixedRows && (
        <button
          type="button"
          onClick={addRow}
          disabled={rows.length >= maxRows}
          className="mt-2 inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] disabled:opacity-40 text-slate-100 px-2.5 py-1 rounded-lg text-xs transition"
        >
          <Plus className="w-3.5 h-3.5" /> Add row
        </button>
      )}
    </div>
  );
}
